import { LoginInput } from "../components/Login/Panel";
import Axios from "axios";
import { IProfile } from "pages/Profile";
import { IEvent, IEventType } from "components/Event";
import { IDevCheckpoint } from "types/grading";
import { IMenuWeek } from "types";
import { handleAPIError, toAPIError } from "utils/handleAPIError";
import { API } from "../constants/api";
import { storage } from "../utils/storage";
import "../i18n";
import { Dayjs } from "dayjs";

Axios.interceptors.request.use((config) => {
  const isAuthEndpoint = config.url?.includes("/login");
  if (isAuthEndpoint) {
    delete config.headers.Authorization;
  } else if (!Axios.defaults.headers.common["Authorization"]) {
    return Promise.reject(new Error("No auth token"));
  }

  const headerSize = JSON.stringify(config.headers).length;
  console.log(`Request headers size: ${headerSize} bytes for ${config.url}`);

  return config;
});

let redirectingToLogin = false;

const buildLoginRedirectUrl = (): string => {
  const path = window.location.pathname + window.location.search;
  const safe =
    path &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.startsWith("/login");
  return safe ? `/login?redirect=${encodeURIComponent(path)}` : "/login";
};

Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? "";
    const method: string = error?.config?.method ?? "";
    const isLoginCall = url.endsWith("/api/login");

    if (status === 401 && !isLoginCall && !redirectingToLogin) {
      redirectingToLogin = true;
      (error as { silentAuth?: boolean }).silentAuth = true;
      console.warn("[auth] forced-logout on 401", { url, method });
      window.sessionStorage.removeItem("auth");
      window.localStorage.removeItem("CapacitorStorage.auth");
      storage.remove("auth").catch(() => {});
      delete Axios.defaults.headers.common["Authorization"];
      if (window.location.pathname !== "/login") {
        window.location.replace(buildLoginRedirectUrl());
      } else {
        setTimeout(() => {
          redirectingToLogin = false;
        }, 5000);
      }
    }
    return Promise.reject(error);
  },
);

export const getProfile = () => {
  return Axios.get<IProfile>(API.PROFILE);
};

export const login = (data: LoginInput) => Axios.post(API.LOGIN, data);

export const updateProfile = (data: IProfile & { removePhoto?: boolean }) => {
  const formData = new FormData();
  if (data.photo) {
    formData.append("photo", data.photo as File);
  }
  formData.append("address", JSON.stringify(data.address));
  formData.append("firstName", data.firstName);
  formData.append("lastName", data.lastName);
  formData.append("email", data.email);
  formData.append("mobile", data.mobile);
  formData.append("removePhoto", data.removePhoto ? "true" : "false");

  return Axios.put(API.PROFILE, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getChildren = async () => {
  try {
    const response = await Axios.post(API.CHILDREN);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const updateChildDietary = async (
  childId: string | number,
  data: {
    allergies: string[];
    allergyOther: string;
    severity: string;
    preferences: string;
    notes: string;
  },
) => {
  try {
    const response = await Axios.put(
      `${API.CHILDREN}/${childId}/dietary`,
      data,
    );
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const confirmChildDietary = async (childId: string | number) => {
  try {
    const response = await Axios.post(
      `${API.CHILDREN}/${childId}/dietary/confirm`,
    );
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getChildDevelopment = async (childId: string | number) => {
  try {
    const response = await Axios.get(`${API.CHILDREN}/${childId}/development`);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const updateChildDevelopment = async (
  childId: string | number,
  data: {
    statuses: { matterId: number; status: number }[];
    notes?: string;
    eventId?: number;
  },
) => {
  try {
    const response = await Axios.put(
      `${API.CHILDREN}/${childId}/development`,
      data,
    );
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const createBill = async (data: unknown) => {
  try {
    const response = await Axios.post(API.BILL_FROM_NURSERY, data);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getNurseryBill = async () => {
  try {
    const response = await Axios.get(API.BILL_GET_NURSERY);
    const res = [{ data: response.data }];
    return res;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getDevArea = async () => {
  try {
    const response = await Axios.get(API.GRADING_DEV_AREA);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getDevAgeBand = async () => {
  try {
    const response = await Axios.get(API.GRADING_DEV_AGE_BAND);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getDevMatter = async (query?: { [prop: string]: unknown }) => {
  try {
    const response = await Axios.get(API.GRADING_DEV_MATTER, {
      params: query,
    });
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getEvents = (query?: { [prop: string]: unknown }) =>
  Axios.get<IEvent[]>(`${API.EVENTS}/`, { params: query });
export const getEventTypes = () => Axios.get<IEventType>(`${API.EVENTS}/types`);

export const getNurseryEvents = () =>
  Axios.get<IEvent[]>(`${API.EVENTS}/nursery`);
export const getReportEvents = (query?: { [prop: string]: unknown }) =>
  Axios.get<IEvent[]>(`${API.EVENTS}/report`, { params: query });
export const getCheckpoint = (query: { areaId: number; ageBandId: number }) =>
  Axios.get<IDevCheckpoint | null>(API.GRADING_CHECKPOINT, { params: query });

interface NewEvent {
  childId: string;
  eventType: string;
  infos: { [prop: string]: unknown };
  images?: File[];
}
export const createEvent = (data: NewEvent) => {
  const formData = new FormData();
  formData.append("childId", data.childId);
  formData.append("eventType", data.eventType);
  formData.append("infos", JSON.stringify(data.infos));
  for (let image of data.images || []) formData.append("images", image);
  return Axios.post(API.EVENTS, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getgallery = () => Axios.get(API.GALLERY);

export const getTransactions = () => Axios.get(API.FINANCE_TRANSACTIONS);

export const getFinanceOverview = () => Axios.get(API.FINANCE);

export const getSetting = async () => Axios.get(API.SETTING);

export const updateNotificationSetting = async (data: {
  [prop: string]: {
    email?: boolean;
    app?: boolean;
    push?: boolean;
  };
}) => {
  try {
    const response = await Axios.put(`${API.SETTING}/notification`, data);

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const updateSessionTimeoutSetting = async (data: string) => {
  try {
    const response = await Axios.put(`${API.SETTING}/session`, {
      sessionTimeout: data,
    });

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const getComments = async (eventId: string) => {
  try {
    const response = await Axios.get(`${API.EVENTS}/${eventId}/comments`);

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const getIndicators = async (eventId: string) => {
  try {
    const response = await Axios.get(`${API.EVENTS}/${eventId}/indicators`);

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const updatePassword = async (data: {
  password: string;
  current_password: string;
}) => {
  try {
    const response = await Axios.put(`${API.PROFILE}/password`, data);

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const createComment = async (id: number, data: { content: string }) => {
  try {
    const response = await Axios.post(`${API.EVENTS}/${id}/comments`, data);

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

interface FormFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  mobile: string;
  line1: string;
  line2: string;
  city: string;
  country: string;
  postcode: string;
  photo?: File;
}

export const addParent = async (data: FormFields) => {
  try {
    const formData = new FormData();
    formData.append("firstName", data.firstName);
    formData.append("lastName", data.lastName);
    formData.append("email", data.email);
    formData.append("mobile", data.mobile);
    formData.append("password", data.password);
    formData.append("line1", data.line1);
    formData.append("line2", data.line2 || "");
    formData.append("city", data.city);
    formData.append("country", data.country);
    formData.append("postcode", data.postcode);
    if (data.photo) formData.append("photo", data.photo);
    const response = await Axios.post(API.PARENTS, formData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

interface NurseryFormFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  mobile: string;
  line1: string;
  line2: string;
  city: string;
  country: string;
  postcode: string;
}

// Despite the name, this hits POST /api/staff — used by AddStaff.tsx for
// staff users (NURSERY_PRACTITIONER / ROOM_LEADER).
export const addNursery = async (data: {
  info: NurseryFormFields;
  role: string;
  dateOfBirth: string;
  classroomIds: number[];
}) => {
  try {
    const formData = new FormData();
    formData.append("firstName", data.info.firstName);
    formData.append("lastName", data.info.lastName);
    formData.append("dateOfBirth", data.dateOfBirth || "");
    formData.append("email", data.info.email);
    formData.append("mobile", data.info.mobile);
    formData.append("password", data.info.password);
    formData.append("line1", data.info.line1);
    formData.append("line2", data.info.line2 || "");
    formData.append("city", data.info.city);
    formData.append("country", data.info.country);
    formData.append("postcode", data.info.postcode);
    formData.append("role", data.role);
    formData.append("classroomIds", JSON.stringify(data.classroomIds ?? []));
    const response = await Axios.post(API.STAFF, formData);
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const getMe = async () => {
  try {
    const me = await Axios.get(API.ME);
    return me.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getBills = async () => {
  try {
    const response = await Axios.get(API.BILL);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

/********************************
 *       Newsletter APIs        *
 ********************************/

interface AddNewsletterData {
  year: string;
  month: string;
  contentResume: string;
  contentMovement: string;
  contentBirthday: string;
  contentEvent: string;
  contentMessage: string;
}

export const addNewsletter = async (data: {
  infos: AddNewsletterData;
  year: Dayjs;
  images?: File[];
}) => {
  try {
    const me = await Axios.get(API.ME);

    const formData = new FormData();

    formData.append("nurseryId", me.data.nurseryId);
    formData.append("year", data.year.format());
    formData.append("month", data.infos.month);
    formData.append("contentResume", data.infos.contentResume);
    formData.append("contentMovement", data.infos.contentMovement);
    formData.append("contentBirthday", data.infos.contentBirthday);
    formData.append("contentEvent", data.infos.contentEvent);
    formData.append("contentMessage", data.infos.contentMessage);

    for (let image of data.images || []) formData.append("images", image);

    const response = await Axios.post(API.NEWSLETTER_ADD_ADMIN, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const getNewsletters = async () => {
  try {
    const response = await Axios.get(API.NEWSLETTER);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getNewsletterById = async (id: string) => {
  const response = await Axios.get(`${API.NEWSLETTER}/${id}`);
  return response.data;
};

interface UpdateNewsletterData {
  id: number;
  year: string;
  month: string;
  contentResume: string;
  contentMovement: string;
  contentBirthday: string;
  contentEvent: string;
  contentMessage: string;
  nurseryId: number;
}

export const updateNewsletter = async (data: {
  infos: UpdateNewsletterData;
  year: Dayjs;
  imgs?: string[];
  images?: File[];
}) => {
  try {
    const formData = new FormData();

    formData.append("id", data.infos.id.toString());
    formData.append("year", data.year.format());
    formData.append("month", data.infos.month);
    formData.append("contentResume", data.infos.contentResume);
    formData.append("contentMovement", data.infos.contentMovement);
    formData.append("contentBirthday", data.infos.contentBirthday);
    formData.append("contentEvent", data.infos.contentEvent);
    formData.append("contentMessage", data.infos.contentMessage);

    for (let img of data.imgs || []) formData.append("imgs", img);

    for (let image of data.images || []) formData.append("images", image);

    const response = await Axios.post(API.NEWSLETTER_UPDATE, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const deleteNewsletter = async (data: { id: number }) => {
  try {
    const response = await Axios.post(API.NEWSLETTER_DELETE, {
      id: data.id,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

/********************************
 *          Menu APIs           *
 ********************************/

interface MenuWeekPayload {
  year: number;
  season: string;
  label: string;
  meals: {
    dayOfWeek: number;
    mealType: string;
    description: string;
    dietaryDescription?: string;
  }[];
  days?: { dayOfWeek: number; vegetarian: boolean; allergens: string[] }[];
}

export const getMenus = async () => {
  try {
    const response = await Axios.get(API.MENU);
    return response.data as {
      weeks: IMenuWeek[];
      workingDays: number[];
    };
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const addMenuWeek = async (data: MenuWeekPayload) => {
  try {
    const response = await Axios.post(API.MENU_ADD, data);
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const updateMenuWeek = async (
  data: MenuWeekPayload & { id: number },
) => {
  try {
    const response = await Axios.post(API.MENU_UPDATE, data);
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const deleteMenuWeek = async (data: { id: number }) => {
  try {
    const response = await Axios.post(API.MENU_DELETE, { id: data.id });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const postMenuDay = async (data: {
  menuWeekId: number;
  dayOfWeek: number;
}) => {
  try {
    const response = await Axios.post(API.MENU_POST_DAY, data);
    return response.data as { count: number };
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

/********************************
 *       Attendance APIs        *
 ********************************/

interface AddAttendanceData {
  child_id: number;
  attendance: number;
  time: "morning" | "afternoon" | "whole_day";
  reason: string;
  comment: string;
}

export const addAttendance = async (data: {
  infos: AddAttendanceData;
  startDate: Dayjs;
  endDate: Dayjs;
}) => {
  try {
    const response = await Axios.put(API.ATTENDANCE_ADD, {
      child_id: data.infos.child_id,
      type: data.infos.attendance,
      time: data.infos.time,
      reason: data.infos.reason,
      comment: data.infos.comment,
      startDate: data.startDate,
      endDate: data.endDate,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

interface UpdateAttendanceData {
  id: number;
  child_id: number;
  attendance: number;
  time: "morning" | "afternoon" | "whole_day";
  reason: string;
  comment: string;
  status: number;
}

export const updateAttendance = async (data: {
  infos: UpdateAttendanceData;
  startDate: Dayjs;
  endDate: Dayjs;
}) => {
  try {
    const response = await Axios.post(API.ATTENDANCE_UPDATE, {
      id: data.infos.id,
      child_id: data.infos.child_id,
      type: data.infos.attendance,
      time: data.infos.time,
      reason: data.infos.reason,
      comment: data.infos.comment,
      startDate: data.startDate,
      endDate: data.endDate,
      status: data.infos.status,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const deleteAttendance = async (data: { id: number }) => {
  try {
    const response = await Axios.post(API.ATTENDANCE_DELETE, {
      id: data.id,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const getAttendances = async () => {
  try {
    const response = await Axios.get(API.ATTENDANCE);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

interface AddChild {
  firstName: string;
  lastName: string;
  gender: string;
  address: string;
  ethnicity: string;
  religion: string;
  languages: string;
  fatherFirstName: string;
  motherFirstName: string;
  thirdFirstName: string;
  fatherLastName: string;
  motherLastName: string;
  thirdLastName: string;
  fatherEmail: string;
  motherEmail: string;
  thirdEmail: string;
  fatherMobile: string;
  motherMobile: string;
  thirdMobile: string;
  nurseryId: number;
  photo: string;
}

export const addChild = async (formData: FormData) => {
  try {
    const response = await Axios.post(API.USERS_CHILD, formData);
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

interface AddClass {
  name: string;
  description: string;
  rangeFrom: number;
  rangeTo: number;
}

export const addClassroom = async (data: AddClass) => {
  try {
    const response = await Axios.post(API.CLASSROOM_ADD, {
      name: data.name,
      description: data.description,
      rangeFrom: data.rangeFrom,
      rangeTo: data.rangeTo,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

interface EditClass {
  id: number;
  name: string;
  description: string;
  rangeFrom: number;
  rangeTo: number;
}

export const editClassroom = async (data: EditClass) => {
  try {
    const response = await Axios.post(API.CLASSROOM_UPDATE, {
      id: data.id,
      name: data.name,
      description: data.description,
      rangeFrom: data.rangeFrom,
      rangeTo: data.rangeTo,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const deleteClassroom = async (id: number) => {
  try {
    const response = await Axios.delete(`${API.CLASSROOM}/${id}`);
    return response.data;
  } catch (error) {
    throw toAPIError(error);
  }
};

type Parent = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  mobile: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
  };
  photo?: string | File;
  settignId: number;
  role: string;
  nurseryId: number;
};

export const updateParent = (data: { info: Parent; setPass: boolean }) => {
  const formData = new FormData();

  if (data.info.photo instanceof File)
    formData.append("photo", data.info.photo);

  formData.append("address", JSON.stringify(data.info.address));
  formData.append("firstName", data.info.firstName);
  formData.append("lastName", data.info.lastName);
  formData.append("email", data.info.email);
  formData.append("mobile", data.info.mobile);
  formData.append("id", data.info.id.toString());
  if (data.setPass) formData.append("password", data.info.password);

  return Axios.put(API.USERS, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const updateParentPassword = async (data: {
  id: number;
  password: string;
}) => {
  try {
    const response = await Axios.post(API.USERS_UPDATE_PASSWORD, data);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const deleteParent = async (data: { id: number }) => {
  try {
    const response = await Axios.post(`${API.USERS}/delete`, data);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const deleteChild = async (data: { id: number }) => {
  try {
    const response = await Axios.post(API.CHILDREN_DELETE_CHILD, data);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

interface UpdateChildData {
  id: string;
  firstName: string;
  lastName: string;
  gender: string;
  address: string;
  additionals: {
    ethnicity: string;
    religion: string;
    languages: string[];
  };
  photo?: string | File;
  fatherFirstName: string;
  motherFirstName: string;
  thirdFirstName: string;
  fatherLastName: string;
  motherLastName: string;
  thirdLastName: string;
  fatherEmail: string;
  motherEmail: string;
  thirdEmail: string;
  fatherMobile: string;
  motherMobile: string;
  thirdMobile: string;
  attendanceMode?: number | string;
}

export const updateChild = (data: {
  info: UpdateChildData;
  parentId: string;
  dateOfBirth: string;
  classroomId: string;
}) => {
  const formData = new FormData();

  if (data.info.photo instanceof File)
    formData.append("photo", data.info.photo);
  if (data.info.id) formData.append("id", data.info.id);

  formData.append("address", data.info.address);
  formData.append("firstName", data.info.firstName);
  formData.append("lastName", data.info.lastName);
  formData.append("gender", data.info.gender);
  formData.append("dateOfBirth", data.dateOfBirth);
  formData.append("additionals", JSON.stringify(data.info.additionals));
  formData.append("fname1", data.info.fatherFirstName || "");
  formData.append("fname2", data.info.motherFirstName || "");
  formData.append("fname3", data.info.thirdFirstName || "");
  formData.append("lname1", data.info.fatherLastName || "");
  formData.append("lname2", data.info.motherLastName || "");
  formData.append("lname3", data.info.thirdLastName || "");
  formData.append("email1", data.info.fatherEmail || "");
  formData.append("email2", data.info.motherEmail || "");
  formData.append("email3", data.info.thirdEmail || "");
  formData.append("mobile1", data.info.fatherMobile || "");
  formData.append("mobile2", data.info.motherMobile || "");
  formData.append("mobile3", data.info.thirdMobile || "");
  formData.append("parentId", data.parentId);
  formData.append("classroomId", data.classroomId);
  if (data.info.attendanceMode != null && data.info.attendanceMode !== "") {
    formData.append("attendanceMode", String(data.info.attendanceMode));
  }

  return Axios.put(API.USERS_CHILD, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getParents = async () => {
  try {
    const response = await Axios.get(API.USERS);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getClassrooms = async () => {
  try {
    const response = await Axios.get(API.CLASSROOM);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getNurserys = async () => {
  try {
    const response = await Axios.get(`${API.USERS}/nursery`);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getCurrencies = async () => {
  try {
    const response = await Axios.get(API.CURRENCY_CURRENCIES);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getRegions = async (countryCode: string) => {
  try {
    const response = await Axios.get(`${API.REGION}/${countryCode}`);
    return response.data;
  } catch {
    return { label: "City", regions: [] };
  }
};

export const getCountries = async () => {
  try {
    const response = await Axios.get(`${API.REGION}/countries`);
    return response.data;
  } catch {
    return [];
  }
};

export interface BillSummaryResponse {
  totalPaidUSD: number;
  totalOutstandingUSD: number;
  filters: {
    currency: string | null;
    month: number | null;
  };
  details: {
    paidCount: number;
    totalBillsCount: number;
  };
}

export const getPaymentSummary = async (params?: {
  currency?: string;
  timeRange?: string;
  summary?: string;
}): Promise<BillSummaryResponse> => {
  try {
    const response = await Axios.get(API.BILL_PAYMENT_SUMMARY, { params });
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

// POST /api/bill/:id/send — admin clicks Send Bill on an existing
// parent_bill row. Stamps sentAt and emails the parent. No body fields:
// the bill is the source of truth for amount/currency/heading.
// Replaces the legacy sendBill that posted to /api/finance/add and
// inserted a duplicate row.
export const sendBillById = async (id: number) => {
  try {
    const response = await Axios.post(`${API.BILL}/${id}/send`);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

interface updateNurseryData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  mobile: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
  };
  classroom: string;
  dateOfBirth: string;
  role: string;
}

export const updateNursery = (data: {
  info: updateNurseryData;
  userId: number;
  dateOfBirth: string;
  classroomIds: number[];
  setPass: boolean;
}) => {
  const formData = new FormData();
  if (data.userId) formData.append("id", data.userId.toString());
  formData.append("address", JSON.stringify(data.info.address));
  formData.append("firstName", data.info.firstName);
  formData.append("lastName", data.info.lastName);
  formData.append("dateOfBirth", data.dateOfBirth);
  formData.append("email", data.info.email);
  formData.append("mobile", data.info.mobile);
  formData.append("classroomIds", JSON.stringify(data.classroomIds ?? []));
  if (data.setPass) {
    formData.append("password", data.info.newPassword);
  }

  return Axios.put(API.USERS_UPDATE_NURSERY, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getBillOverview = () => Axios.get(API.BILL);
export const getTransactionView = () => Axios.get(API.BILL_TRANSACTIONS);

export const invoiceCheckout = async (data: { id: number; type: string }) => {
  try {
    const response = await Axios.post(API.BILL_CHECKOUT, data);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const invoiceParentCheckout = async (data: {
  id: number;
  type: string;
}) => {
  try {
    const response = await Axios.post(API.BILL_CHECKOUT_PARENT, data);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getWithdraws = async () => {
  try {
    const response = await Axios.get(API.WITHDRAW);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

interface AddWithdraw {
  amount: number;
  accountName: string;
  routingNumber: string;
  accountNumber: string;
  currency: string;
  comment: string;
}

export const addWithdraw = async (data: AddWithdraw) => {
  try {
    const response = await Axios.post(API.WITHDRAW_ADD, {
      amount: data.amount,
      accountName: data.accountName,
      routingNumber: data.routingNumber,
      accountNumber: data.accountNumber,
      currency: data.currency,
      comment: data.comment,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

/********************************
 *          Report APIs         *
 ********************************/

export const addReport = async (data: { id: number; blob: Blob }) => {
  try {
    const formData = new FormData();

    const fileOfBlob = new File([data.blob], "report.pdf");

    formData.append("id", data.id.toString());
    formData.append("blob_file", fileOfBlob);

    const response = await Axios.post(API.REPORT_ADD, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(handleAPIError(error).message);
  }
};

export const getReports = async () => {
  try {
    const response = await Axios.get(API.REPORT);
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};

export const getReportsPDF = async (query?: { [prop: string]: unknown }) => {
  try {
    const response = await Axios.get(API.REPORT_DOWNLOAD, { params: query });
    return response.data;
  } catch (err) {
    throw new Error(handleAPIError(err).message);
  }
};
